import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';

const TimePicker = ({ 
  value, 
  onChange, 
  label,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Parse the value (HH:MM format)
  const parseTime = (timeStr) => {
    if (!timeStr) return { hours: 12, minutes: 0 };
    const [hours, minutes] = timeStr.split(':').map(Number);
    return { hours: hours || 0, minutes: minutes || 0 };
  };

  const { hours, minutes } = parseTime(value);

  // Format time for display
  const formatDisplay = (h, m) => {
    const period = h >= 12 ? 'م' : 'ص';
    const displayHour = h % 12 || 12;
    return `${String(displayHour).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
  };

  // Format time for value (24-hour format)
  const formatValue = (h, m) => {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const incrementHours = () => {
    const newHours = (hours + 1) % 24;
    onChange(formatValue(newHours, minutes));
  };

  const decrementHours = () => {
    const newHours = (hours - 1 + 24) % 24;
    onChange(formatValue(newHours, minutes));
  };

  const incrementMinutes = () => {
    const newMinutes = (minutes + 5) % 60;
    onChange(formatValue(hours, newMinutes));
  };

  const decrementMinutes = () => {
    const newMinutes = (minutes - 5 + 60) % 60;
    onChange(formatValue(hours, newMinutes));
  };

  const setTime = (h, m) => {
    onChange(formatValue(h, m));
  };

  // Quick time options
  const quickTimes = [
    { label: 'الآن', action: () => {
      const now = new Date();
      setTime(now.getHours(), now.getMinutes());
    }},
    { label: '08:00 ص', hours: 8, minutes: 0 },
    { label: '09:00 ص', hours: 9, minutes: 0 },
    { label: '10:00 ص', hours: 10, minutes: 0 },
    { label: '11:00 ص', hours: 11, minutes: 0 },
    { label: '12:00 م', hours: 12, minutes: 0 },
    { label: '01:00 م', hours: 13, minutes: 0 },
    { label: '02:00 م', hours: 14, minutes: 0 },
    { label: '03:00 م', hours: 15, minutes: 0 },
    { label: '04:00 م', hours: 16, minutes: 0 },
    { label: '05:00 م', hours: 17, minutes: 0 },
  ];

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}
      
      {/* Input Display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl cursor-pointer bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 flex items-center justify-between"
      >
        <span className="text-gray-900 font-medium" dir="ltr">
          {formatDisplay(hours, minutes)}
        </span>
        <Clock className="w-5 h-5 text-gray-400" />
      </div>

      {/* Time Picker Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-[280px]" style={{ left: 0 }}>
          {/* Time Spinners */}
          <div className="flex items-center justify-center gap-4 mb-4">
            {/* Minutes */}
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={incrementMinutes}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronUp className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-16 h-14 flex items-center justify-center bg-gray-50 rounded-xl">
                <span className="text-2xl font-bold text-gray-800">{String(minutes).padStart(2, '0')}</span>
              </div>
              <button
                type="button"
                onClick={decrementMinutes}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronDown className="w-5 h-5 text-gray-600" />
              </button>
              <span className="text-xs text-gray-500 mt-1">دقيقة</span>
            </div>

            <span className="text-2xl font-bold text-gray-400">:</span>

            {/* Hours */}
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={incrementHours}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronUp className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-16 h-14 flex items-center justify-center bg-blue-50 rounded-xl border-2 border-blue-200">
                <span className="text-2xl font-bold text-blue-600">{String(hours % 12 || 12).padStart(2, '0')}</span>
              </div>
              <button
                type="button"
                onClick={decrementHours}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronDown className="w-5 h-5 text-gray-600" />
              </button>
              <span className="text-xs text-gray-500 mt-1">ساعة</span>
            </div>

            {/* AM/PM */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  if (hours >= 12) setTime(hours - 12, minutes);
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  hours < 12 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                ص
              </button>
              <button
                type="button"
                onClick={() => {
                  if (hours < 12) setTime(hours + 12, minutes);
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  hours >= 12 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                م
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 my-3"></div>

          {/* Quick Times */}
          <div className="grid grid-cols-4 gap-2">
            {quickTimes.map((time, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  if (time.action) {
                    time.action();
                  } else {
                    setTime(time.hours, time.minutes);
                  }
                  setIsOpen(false);
                }}
                className="px-2 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {time.label}
              </button>
            ))}
          </div>

          {/* Confirm Button */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full mt-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors"
          >
            تأكيد
          </button>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
